// frontend/src/pages/Annuaire.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Card, CardBody, Avatar, VStack, HStack,
  Badge, Spinner, Flex, Input, InputGroup, InputLeftElement, Divider,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { apiService } from '../services/apiService';
import { getPhotoUrl } from '../config/image';

function MemberCard({ membre }) {
  const groupes = membre.groupes_noms ? membre.groupes_noms.split(', ') : [];
  return (
    <Card size="sm" _hover={{ shadow: 'md' }} transition="box-shadow 0.2s">
      <CardBody>
        <VStack spacing={3}>
          <Avatar
            size="lg"
            name={`${membre.prenom} ${membre.nom}`}
            src={membre.photo ? getPhotoUrl(membre.photo) : undefined}
          />
          <VStack spacing={1} textAlign="center">
            <Text fontWeight="bold" fontSize="sm">{membre.prenom} {membre.nom}</Text>
            {membre.fonction_nom && (
              <Badge colorScheme="green" fontSize="0.65em">{membre.fonction_nom}</Badge>
            )}
          </VStack>
          {groupes.length > 0 && (
            <HStack spacing={1} flexWrap="wrap" justify="center">
              {groupes.map((g) => (
                <Badge key={g} colorScheme="gray" variant="subtle" fontSize="0.6em">{g}</Badge>
              ))}
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}

export default function Annuaire() {
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchAnnuaire = async () => {
      try {
        const data = await apiService.makeRequest('/members/annuaire');
        setMembres(data.membres || []);
      } catch (err) {
        console.error('Erreur chargement annuaire:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnuaire();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return membres;
    return membres.filter(m =>
      `${m.prenom} ${m.nom}`.toLowerCase().includes(q) ||
      (m.fonction_nom || '').toLowerCase().includes(q) ||
      (m.groupes_noms || '').toLowerCase().includes(q)
    );
  }, [membres, search]);

  const sansAccents = (str) => (str || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');

  const matchAny = (str, mots) => {
    const n = sansAccents(str);
    return mots.some((mot) => n.includes(mot));
  };

  const isBureau = (m) => matchAny(m.fonction_nom, [
    'president', 'secretaire', 'tresorier', 'pasteur',
    'commissaire au compte', 'commissaire aux comptes',
  ]);
  const isAncien = (m) => matchAny(m.fonction_nom, ['ancien']) || matchAny(m.groupes_noms, ['ancien']);
  const isConseiller = (m) => matchAny(m.groupes_noms, ['conseiller']) || matchAny(m.fonction_nom, ['conseiller']);

  const bureauPriorite = (m) => {
    const n = sansAccents(m.fonction_nom);
    if (n.includes('president')) return 0;
    if (n.includes('pasteur')) return 1;
    return 2;
  };
  const bureau = filtered.filter(isBureau).sort((a, b) => bureauPriorite(a) - bureauPriorite(b));
  const anciens = filtered.filter(m => !isBureau(m) && isAncien(m));
  const conseillers = filtered.filter(m => !isBureau(m) && !isAncien(m) && isConseiller(m));
  const autresMembres = filtered.filter(m => !isBureau(m) && !isAncien(m) && !isConseiller(m));

  if (loading) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="xl" color="green.500" />
      </Flex>
    );
  }

  return (
    <Box maxW="1100px" mx="auto">
      <Heading size="lg" mb={2}>📖 Annuaire des membres</Heading>
      <Text color="gray.500" mb={6}>Trombinoscope de la communauté EECF Paris</Text>

      <InputGroup mb={8} maxW="400px">
        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
        <Input
          placeholder="Rechercher un membre, une fonction, un groupe..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg="white"
        />
      </InputGroup>

      {bureau.length > 0 && (
        <Box mb={10}>
          <Heading size="md" color="green.600" mb={4}>🏛️ Bureau</Heading>
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
            {bureau.map((m) => <MemberCard key={m.id} membre={m} />)}
          </SimpleGrid>
        </Box>
      )}

      {anciens.length > 0 && (
        <>
          <Divider mb={8} />
          <Box mb={10}>
            <Heading size="md" color="green.600" mb={4}>⛪ Anciens d'église</Heading>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
              {anciens.map((m) => <MemberCard key={m.id} membre={m} />)}
            </SimpleGrid>
          </Box>
        </>
      )}

      {conseillers.length > 0 && (
        <>
          <Divider mb={8} />
          <Box mb={10}>
            <Heading size="md" color="green.600" mb={4}>🤝 Conseillers paroissiaux</Heading>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
              {conseillers.map((m) => <MemberCard key={m.id} membre={m} />)}
            </SimpleGrid>
          </Box>
        </>
      )}

      <Divider mb={8} />

      <Box>
        <Heading size="md" color="gray.700" mb={4}>👥 Membres</Heading>
        {autresMembres.length === 0 ? (
          <Text color="gray.500">Aucun membre trouvé.</Text>
        ) : (
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
            {autresMembres.map((m) => <MemberCard key={m.id} membre={m} />)}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
}
